import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { partyText } from "../../static/materialLine";
import { PartyOptionProps } from "../../types/materialLine";
import {
  TurningReceiveDateBy,
  TurningReceiveListProps,
  TurningReceiveProps,
  TurningReceiveSortBy,
  TurningReceiveSortOrder,
  OpenTurningOrderProps,
} from "../../types/turningReceive";

export interface TurningReceivesState {
  receives: TurningReceiveListProps[];
  totalReceives: number;
  currentReceive: TurningReceiveProps | null;
  currentReceiveLoading: boolean;
  partyOptions: PartyOptionProps[];
  filterPartyOptions: PartyOptionProps[];
  openOrders: OpenTurningOrderProps[];
  openOrdersLoading: boolean;
  openOrdersRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: TurningReceivesState = {
  receives: [],
  totalReceives: 0,
  currentReceive: null,
  currentReceiveLoading: false,
  partyOptions: [],
  filterPartyOptions: [],
  openOrders: [],
  openOrdersLoading: false,
  openOrdersRequestId: null,
  loading: false,
  error: null,
};

const defaultError = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type FetchTurningReceiveListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  party_id?: number | null;
  date_by?: TurningReceiveDateBy;
  sort_by?: TurningReceiveSortBy;
  sort_order?: TurningReceiveSortOrder;
};

export const fetchTurningReceiveListData = createAsyncThunk(
  "fetchTurningReceiveListData",
  async (req: FetchTurningReceiveListDataProps) => {
    let response: any;
    await GET(`/v1/admin/turning-receives`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchTurningReceiveDetail = createAsyncThunk(
  "fetchTurningReceiveDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/turning-receives/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The parties with open orders; partyId keeps an edited receive's own party in the list.
export const fetchTurningReceiveParties = createAsyncThunk(
  "fetchTurningReceiveParties",
  async (partyId?: number | null) => {
    let response: any;
    await GET(`/v1/admin/turning-receives/parties`, {
      party_id: partyId || null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The list filter's parties: every turning party, active or not, and every party that has receives.
export const fetchTurningReceiveFilterParties = createAsyncThunk(
  "fetchTurningReceiveFilterParties",
  async () => {
    let response: any;
    await GET(`/v1/admin/turning-receives/parties`, { all: 1 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The party's open orders for the receive form; receive_id keeps an edited receive's own orders and leaves out its
// own pieces.
export const fetchTurningReceiveOpenOrders = createAsyncThunk(
  "fetchTurningReceiveOpenOrders",
  async (req: { party_id: number; receive_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/turning-receives/orders`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const turningReceivesSlice = createSlice({
  name: "turningReceives",
  initialState,
  reducers: {
    clearCurrentTurningReceive: (state) => {
      state.currentReceive = null;
      state.currentReceiveLoading = false;
    },
    clearTurningReceiveOpenOrders: (state) => {
      state.openOrders = [];
      state.openOrdersLoading = false;
      state.openOrdersRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTurningReceiveListData.pending, (state) => {
      state.receives = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchTurningReceiveListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.receives = action.payload.data.data.records;
          state.totalReceives = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.receives = [];
          state.error = action?.payload?.data?.message || defaultError;
        }
      }
    );
    builder.addCase(
      fetchTurningReceiveListData.rejected,
      (state, action: any) => {
        state.receives = [];
        state.loading = false;
        state.error = action?.payload?.data?.message || defaultError;
      }
    );

    builder.addCase(fetchTurningReceiveDetail.pending, (state) => {
      state.currentReceiveLoading = true;
      state.currentReceive = null;
    });
    builder.addCase(
      fetchTurningReceiveDetail.fulfilled,
      (state, action: any) => {
        state.currentReceiveLoading = false;
        if (action.payload.data.status === "success") {
          state.currentReceive = action.payload.data.data;
        }
      }
    );
    builder.addCase(fetchTurningReceiveDetail.rejected, (state) => {
      state.currentReceiveLoading = false;
      state.currentReceive = null;
    });

    builder.addCase(
      fetchTurningReceiveParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.partyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    builder.addCase(
      fetchTurningReceiveFilterParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.filterPartyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    // Only the latest request counts, so a slow answer for a party picked earlier can't overwrite it.
    builder.addCase(fetchTurningReceiveOpenOrders.pending, (state, action) => {
      state.openOrders = [];
      state.openOrdersLoading = true;
      state.openOrdersRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchTurningReceiveOpenOrders.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.openOrdersRequestId) return;
        state.openOrdersLoading = false;
        state.openOrders =
          action.payload.data.status === "success"
            ? action.payload.data.data.records
            : [];
      }
    );
    builder.addCase(
      fetchTurningReceiveOpenOrders.rejected,
      (state, action) => {
        if (action.meta.requestId !== state.openOrdersRequestId) return;
        state.openOrdersLoading = false;
      }
    );
  },
});

export const { clearCurrentTurningReceive, clearTurningReceiveOpenOrders } =
  turningReceivesSlice.actions;

export const getTurningReceives = (state: RootState) =>
  state.turningReceives.receives;
export const getTotalTurningReceives = (state: RootState) =>
  state.turningReceives.totalReceives;
export const getCurrentTurningReceive = (state: RootState) =>
  state.turningReceives.currentReceive;
export const getCurrentTurningReceiveLoading = (state: RootState) =>
  state.turningReceives.currentReceiveLoading;
export const getTurningReceivePartyOptions = (state: RootState) =>
  state.turningReceives.partyOptions;
export const getTurningReceiveFilterPartyOptions = (state: RootState) =>
  state.turningReceives.filterPartyOptions;
export const getTurningReceiveOpenOrders = (state: RootState) =>
  state.turningReceives.openOrders;
export const getTurningReceiveOpenOrdersLoading = (state: RootState) =>
  state.turningReceives.openOrdersLoading;

export default turningReceivesSlice.reducer;
